import React, { useEffect, useMemo, useState } from "react";
import { Modal, Checkbox } from "antd";
import { ZoomInOutlined } from "@ant-design/icons";
import { Form } from "@dreambigger/design-system/src/components";
import { DocumentStepAssets } from "@dreambigger/shared/src/types";
import { StepProps } from "../pages/flows/[flowId]";
import StepWrapper from "./step-wrapper";
import { Document as PdfDocument, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { useApplication } from "../api";
import styles from "./steps.module.scss";

// — pull in the ESM‐formatted worker directly
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export const CONFIRMATION_KEY = "documentConfirmation";

export default function Document({ flow, step, brand, progress }: StepProps) {
  const [form] = Form.useForm();
  const [disabled, setDisabled] = useState<boolean>(true);
  const [numPages, setNumPages] = useState<number>(0);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [pagesLoaded, setPagesLoaded] = useState<boolean>(false);

  const assets: DocumentStepAssets = step.assets as DocumentStepAssets;
  const { documentUrl, documentTitle, confirmationText } = assets;

  const applicationHelper = useApplication(flow.financialInstitution.id);
  const application = useMemo(
    () => applicationHelper.find(flow.id, "draft"),
    [applicationHelper, flow]
  );
  const response = useMemo(
    () => applicationHelper.findResponse(flow.id, "draft", step.slug),
    [application, flow, step]
  );
  const onDocumentLoadSuccess = ({ numPages: total }: { numPages: number }) => {
    setNumPages(total);
  };

  const onDocumentLoadError = (error: any) => {
    console.error("PDF load error:", error);
  };

  useEffect(() => {
    if (!application) return;
    const confirmationValue = response?.fields[CONFIRMATION_KEY];
    if (confirmationValue !== undefined) {
      setDisabled(confirmationValue !== true);
      form.setFieldsValue({
        [CONFIRMATION_KEY]: confirmationValue,
      });
    } else {
      setDisabled(false);
    }
  }, [application, response, form]);

  const wrapperProps = { flow, step, brand, progress, form, disabled };

  return (
    <StepWrapper {...wrapperProps}>
      <label className="db mb-2">{documentTitle}</label>
      <div
        className="primary f-4 fwt pointer"
        onClick={() => setShowModal(true)}
      >
        <ZoomInOutlined className="mr-2" />
        View Full Screen
      </div>
      <div
        style={{
          width: "100%",
          maxWidth: 650,
          height: "40vh",
          overflowY: "auto",
        }}
      >
        <PdfDocument
          file={documentUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          className="thumbnail-responsive"
        >
          {Array.from({ length: numPages }, (_, i) => (
            // Map all pages. Hide their visibility until loaded.
            <div key={i + 1} className={`${!pagesLoaded && "vh"}`}>
              {i !== 0 && (
                <div className="primary f-1 fwt mv-2 pl-4">Page {i + 1}</div>
              )}
              <Page
                pageNumber={i + 1}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                onLoadSuccess={() => setPagesLoaded(true)}
              />
            </div>
          ))}
        </PdfDocument>
      </div>
      {confirmationText && (
        <div className="mt-6 flex items-center">
          <Form.Item
            name={CONFIRMATION_KEY}
            valuePropName="checked"
            className="mb-0 mr-3"
          >
            <Checkbox
              className="ao-lg-checkbox"
              onClick={() => setDisabled(!disabled)}
            />
          </Form.Item>
          <p
            className={`${styles.textLinks} f-4 mb-0`}
            dangerouslySetInnerHTML={{
              __html: confirmationText,
            }}
          />
        </div>
      )}

      {/* Modal view */}
      <Modal
        open={showModal}
        onCancel={() => setShowModal(false)}
        footer={null}
        width="100vw"
        style={{ top: 0, padding: 0 }}
        styles={{
          body: { height: "100vh", padding: 0 },
          content: { padding: 0 },
        }}
        maskClosable={false}
        destroyOnClose={false}
      >
        <div style={{ height: "100%", overflowY: "auto", padding: 16 }}>
          <PdfDocument
            file={documentUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            options={{ cMapUrl: `/cmaps/`, cMapPacked: true }}
            className="modal-responsive"
          >
            {Array.from({ length: numPages }, (_, i) => (
              // Map all pages. In this view, it is fine to show the loading state for each page, so class "vh" is not needed.
              <React.Fragment key={i + 1}>
                {i !== 0 && (
                  <div className="primary f-1 fwb mv-2 pl-3 bt b-gray-4">
                    Page {i + 1}
                  </div>
                )}
                <Page
                  pageNumber={i + 1}
                  renderTextLayer
                  renderAnnotationLayer
                />
              </React.Fragment>
            ))}
          </PdfDocument>
        </div>

        {/* Create classes without a dedicated file */}
        <style jsx global>{`
          .thumbnail-responsive canvas,
          .react-pdf__Page__canvas {
            width: 100% !important;
            height: auto !important;
          }
          .react-pdf__Page__canvas {
            max-width: 100% !important;
            height: auto !important;
          }
        `}</style>
      </Modal>
    </StepWrapper>
  );
}
